import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaEnvelope, FaUser, FaBirthdayCake, FaPhone, FaShieldAlt, FaPaperPlane, FaCheckCircle, FaTimesCircle, FaClock } from 'react-icons/fa';
import { sendGeneralEnquiry, sendWelfareEnquiry } from '../services/emailService';
import Seo from '../components/Seo';

function Contact() {
  const [formData, setFormData] = useState({
    parentFirstName: '',
    parentLastName: '',
    swimmerFullName: '',
    dateOfBirth: '',
    phone: '',
    email: '',
    message: ''
  });
  const [welfareFormData, setWelfareFormData] = useState({
    name: '',
    email: '',
    phone: '',
    relationship: '',
    concernType: '',
    message: '',
    anonymous: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [isWelfareSubmitting, setIsWelfareSubmitting] = useState(false);
  const [welfareSubmitStatus, setWelfareSubmitStatus] = useState('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState(0);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [honeypot, setHoneypot] = useState('');
  const [welfareHoneypot, setWelfareHoneypot] = useState('');
  const [formLoadedAt] = useState(() => Date.now());

  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#welfare-officer') {
      setTimeout(() => {
        const element = document.getElementById('welfare-officer');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleWelfareChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWelfareFormData({
      ...welfareFormData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime;

    if (timeSinceLastSubmission < 30000) {
      setSubmitStatus('rate-limited');
      return;
    }

    if (timeSinceLastSubmission > 3600000) {
      setSubmissionCount(0);
    }

    if (submissionCount >= 5) {
      setSubmitStatus('rate-limited');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');
    setLastSubmissionTime(now);
    setSubmissionCount(prev => prev + 1);

    try {
      await sendGeneralEnquiry({ ...formData, website: honeypot, formLoadedAt });
      setSubmitStatus('success');
      setFormData({
        parentFirstName: '',
        parentLastName: '',
        swimmerFullName: '',
        dateOfBirth: '',
        phone: '',
        email: '',
        message: ''
      });
    } catch (error) {
      console.error('Email send failed:', error);
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  const handleWelfareSubmit = async (e) => {
    e.preventDefault();

    const now = Date.now();
    const timeSinceLastSubmission = now - lastSubmissionTime;

    if (timeSinceLastSubmission < 30000) {
      setWelfareSubmitStatus('rate-limited');
      return;
    }

    if (timeSinceLastSubmission > 3600000) {
      setSubmissionCount(0);
    }

    if (submissionCount >= 5) {
      setWelfareSubmitStatus('rate-limited');
      return;
    }

    setIsWelfareSubmitting(true);
    setWelfareSubmitStatus('');
    setLastSubmissionTime(now);
    setSubmissionCount(prev => prev + 1);

    try {
      await sendWelfareEnquiry({ ...welfareFormData, website: welfareHoneypot, formLoadedAt });
      setWelfareSubmitStatus('success');
      setWelfareFormData({
        name: '',
        email: '',
        phone: '',
        relationship: '',
        concernType: '',
        message: '',
        anonymous: false
      });
    } catch (error) {
      console.error('Welfare email send failed:', error);
      setWelfareSubmitStatus('error');
    }

    setIsWelfareSubmitting(false);
  };

  return (
    <main className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <Seo
        title="Contact Us"
        description="Get in touch with Marple Swimming Club for enquiries about joining, membership, or to contact our welfare officer."
        path="/contact"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-display text-5xl font-bold text-navy-900 mb-6">Contact Us</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Ready to dive in? Get in touch with us to learn more about our programs,
            schedule a tour, or ask any questions you might have.
          </p>

          {/* Contact Type Cards */}
          <div className="flex gap-6 justify-center mt-8 flex-wrap">
            <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-500 rounded-xl p-6 text-center min-w-[250px] shadow-lg">
              <FaEnvelope className="text-3xl text-red-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-red-700 mb-2">General Enquiries</h3>
              <p className="text-gray-700 text-sm">
                Programs, membership, swimming lessons
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-500 rounded-xl p-6 text-center min-w-[250px] shadow-lg">
              <FaShieldAlt className="text-3xl text-blue-600 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-blue-700 mb-2">Welfare Officer</h3>
              <p className="text-gray-700 text-sm mb-2">
                Safeguarding, welfare concerns
              </p>
              <a
                href="#welfare-officer"
                className="text-blue-600 text-sm font-semibold hover:underline"
              >
                Jump to form ↓
              </a>
            </div>
          </div>
        </motion.div>

        {/* General Enquiry Form */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
              {/* Honeypot - hidden from real users, bots often fill every field */}
              <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <h2 className="font-display text-3xl font-bold text-navy-900 text-center mb-6">Send us a Message</h2>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="flex items-center justify-center gap-2 bg-green-50 border-2 border-green-500 text-green-800 p-4 rounded-xl mb-6 text-center">
                  <FaCheckCircle className="flex-shrink-0" />
                  Thank you! Your message has been sent successfully.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="flex items-center justify-center gap-2 bg-red-50 border-2 border-red-500 text-red-800 p-4 rounded-xl mb-6 text-center">
                  <FaTimesCircle className="flex-shrink-0" />
                  Sorry, there was an error. Please try again.
                </div>
              )}

              {submitStatus === 'rate-limited' && (
                <div className="flex items-center justify-center gap-2 bg-amber-50 border-2 border-amber-500 text-amber-800 p-4 rounded-xl mb-6 text-center">
                  <FaClock className="flex-shrink-0" />
                  Please wait before submitting another form.
                </div>
              )}

              {/* Name Fields */}
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    <FaUser className="inline mr-2 text-primary-600" />
                    Parent's First Name *
                  </label>
                  <input
                    type="text"
                    name="parentFirstName"
                    value={formData.parentFirstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    <FaUser className="inline mr-2 text-primary-600" />
                    Parent's Last Name *
                  </label>
                  <input
                    type="text"
                    name="parentLastName"
                    value={formData.parentLastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              {/* Swimmer Name */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  <FaUser className="inline mr-2 text-primary-600" />
                  Swimmer's Full Name *
                </label>
                <input
                  type="text"
                  name="swimmerFullName"
                  value={formData.swimmerFullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                />
              </div>

              {/* Date of Birth */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  <FaBirthdayCake className="inline mr-2 text-primary-600" />
                  Date of Birth *
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                />
              </div>

              {/* Phone */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  <FaPhone className="inline mr-2 text-primary-600" />
                  Contact Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                />
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  <FaEnvelope className="inline mr-2 text-primary-600" />
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                />
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="block text-gray-700 font-semibold mb-2">
                  Message (include swimming ability) *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Please tell us about the swimmer's current swimming ability (e.g., beginner, can swim 25m, competitive experience, etc.) and any questions you have..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-primary-500 focus:outline-none transition-colors"
                ></textarea>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary-600 text-white py-4 px-6 rounded-full font-bold text-lg hover:bg-primary-700 disabled:bg-gray-400 transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
              >
                <FaPaperPlane />
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </motion.section>

        {/* Welfare Officer Form */}
        <motion.section
          id="welfare-officer"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="pt-8 border-t-4 border-gray-200"
        >
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleWelfareSubmit} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-xl p-8 border-2 border-blue-500">
              {/* Honeypot - hidden from real users, bots often fill every field */}
              <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} aria-hidden="true">
                <input
                  type="text"
                  name="website"
                  value={welfareHoneypot}
                  onChange={(e) => setWelfareHoneypot(e.target.value)}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div className="text-center mb-6">
                <FaShieldAlt className="text-5xl text-blue-600 mx-auto mb-4" />
                <h2 className="font-display text-3xl font-bold text-blue-700 mb-3">Contact Welfare Officer</h2>
                <p className="text-gray-700">
                  For any welfare concerns, safeguarding issues, or confidential matters affecting swimmers or club members.
                </p>
              </div>

              {/* Status Messages */}
              {welfareSubmitStatus === 'success' && (
                <div className="flex items-center justify-center gap-2 bg-green-50 border-2 border-green-500 text-green-800 p-4 rounded-xl mb-6 text-center">
                  <FaCheckCircle className="flex-shrink-0" />
                  Thank you! Your message has been sent to our Welfare Officer.
                </div>
              )}

              {welfareSubmitStatus === 'error' && (
                <div className="flex items-center justify-center gap-2 bg-red-50 border-2 border-red-500 text-red-800 p-4 rounded-xl mb-6 text-center">
                  <FaTimesCircle className="flex-shrink-0" />
                  Sorry, there was an error. Please try again.
                </div>
              )}

              {welfareSubmitStatus === 'rate-limited' && (
                <div className="flex items-center justify-center gap-2 bg-amber-50 border-2 border-amber-500 text-amber-800 p-4 rounded-xl mb-6 text-center">
                  <FaClock className="flex-shrink-0" />
                  Please wait before submitting another form.
                </div>
              )}

              {/* Anonymous Checkbox */}
              <div className="mb-4 bg-white p-4 rounded-lg">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="anonymous"
                    checked={welfareFormData.anonymous}
                    onChange={handleWelfareChange}
                    className="w-5 h-5 text-blue-600 mr-3"
                  />
                  <span className="text-gray-800 font-semibold">Submit anonymously</span>
                </label>
              </div>

              {/* Contact Details (if not anonymous) */}
              {!welfareFormData.anonymous && (
                <>
                  <div className="mb-4">
                    <label className="block text-gray-800 font-semibold mb-2">Your Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={welfareFormData.name}
                      onChange={handleWelfareChange}
                      required={!welfareFormData.anonymous}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-800 font-semibold mb-2">Your Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={welfareFormData.email}
                      onChange={handleWelfareChange}
                      required={!welfareFormData.anonymous}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-gray-800 font-semibold mb-2">Your Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={welfareFormData.phone}
                      onChange={handleWelfareChange}
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                    />
                  </div>
                </>
              )}

              {/* Relationship */}
              <div className="mb-4">
                <label className="block text-gray-800 font-semibold mb-2">Your Relationship to the Club *</label>
                <select
                  name="relationship"
                  value={welfareFormData.relationship}
                  onChange={handleWelfareChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                >
                  <option value="">Please select...</option>
                  <option value="parent">Parent/Guardian</option>
                  <option value="swimmer">Swimmer</option>
                  <option value="coach">Coach/Staff</option>
                  <option value="volunteer">Volunteer</option>
                  <option value="committee">Committee Member</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Concern Type */}
              <div className="mb-4">
                <label className="block text-gray-800 font-semibold mb-2">Type of Concern *</label>
                <select
                  name="concernType"
                  value={welfareFormData.concernType}
                  onChange={handleWelfareChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                >
                  <option value="">Please select...</option>
                  <option value="safeguarding">Safeguarding Concern</option>
                  <option value="bullying">Bullying or Harassment</option>
                  <option value="inappropriate-behavior">Inappropriate Behavior</option>
                  <option value="discrimination">Discrimination</option>
                  <option value="welfare-general">General Welfare Concern</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Message */}
              <div className="mb-6">
                <label className="block text-gray-800 font-semibold mb-2">Details of Your Concern *</label>
                <textarea
                  name="message"
                  value={welfareFormData.message}
                  onChange={handleWelfareChange}
                  required
                  rows="6"
                  placeholder="Please provide details about your concern. All information will be treated confidentially and handled according to our safeguarding policies."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                ></textarea>
              </div>

              {/* Confidentiality Notice */}
              <div className="bg-white p-4 rounded-lg mb-6 border-l-4 border-blue-600">
                <p className="text-sm text-gray-700">
                  <strong>Confidentiality Notice:</strong> All welfare concerns are treated with utmost confidentiality and handled according to our safeguarding policies. For urgent concerns, please speak to club staff immediately.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isWelfareSubmitting}
                className="w-full bg-blue-600 text-white py-4 px-6 rounded-full font-bold text-lg hover:bg-blue-700 disabled:bg-gray-400 transition-all duration-300 shadow-lg flex items-center justify-center gap-2"
              >
                <FaShieldAlt />
                {isWelfareSubmitting ? 'Sending...' : 'Send to Welfare Officer'}
              </button>
            </form>
          </div>
        </motion.section>
      </div>
    </main>
  );
}

export default Contact;
